/**
 * RangeBar — Zeitraum-Auswahl (Presets + freier Zeitraum).
 * Mobil: 3×2-Raster statt Wischleiste (Projektregel: kein horizontales Scrollen).
 */
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RANGE_PRESETS } from '@/lib/dashboardAnalytics';

const iso = (d) => format(d, 'yyyy-MM-dd');
const human = (d) => format(d, 'dd.MM.yyyy', { locale: de });

export default function RangeBar({ range, preset, rawFrom, rawTo, onChange }) {
  const select = (id) => {
    // Beim Wechsel auf "Zeitraum" den aktuellen Bereich als Startwert übernehmen
    if (id === 'custom') onChange({ preset: 'custom', from: iso(range.from), to: iso(range.to) });
    else onChange({ preset: id });
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
      <div
        role="radiogroup"
        aria-label="Zeitraum"
        className="grid grid-cols-3 sm:grid-cols-6 lg:inline-grid lg:grid-flow-col lg:auto-cols-max gap-1 p-1 bg-slate-100 rounded-lg"
      >
        {RANGE_PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => select(p.id)}
              className={cn(
                'h-9 px-2 sm:px-3 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 active:scale-[0.98]',
                active ? 'bg-white text-brand-950 shadow-sm' : 'text-slate-500 hover:text-slate-900'
              )}
            >
              <span className="sm:hidden">{p.short || p.label}</span>
              <span className="hidden sm:inline">{p.label}</span>
            </button>
          );
        })}
      </div>

      {preset === 'custom' ? (
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-end">
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Von
            <input
              type="date"
              value={rawFrom}
              max={rawTo || undefined}
              onChange={(e) => e.target.value && onChange({ preset: 'custom', from: e.target.value })}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-normal normal-case tracking-normal text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Bis
            <input
              type="date"
              value={rawTo}
              min={rawFrom || undefined}
              onChange={(e) => e.target.value && onChange({ preset: 'custom', to: e.target.value })}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-normal normal-case tracking-normal text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
        </div>
      ) : (
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 tabular-nums">
          <CalendarRange className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span className="whitespace-nowrap">{human(range.from)} – {human(range.to)}</span>
          <span className="text-slate-300 hidden sm:inline" aria-hidden="true">·</span>
          <span className="whitespace-nowrap">Vergleich: {human(range.prevFrom)} – {human(range.prevTo)}</span>
        </p>
      )}
    </div>
  );
}
